import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import * as analytics from '../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdvertiserAuthContext = createContext(null);

export function AdvertiserAuthProvider({ children }) {
  // Fix 7: sessionStorage — cleared when the browser tab closes, preventing XSS token theft
  const [advertiser, setAdvertiser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem('advertiser_token');
    if (token) {
      axios.get(`${API_URL}/api/advertiser/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setAdvertiser(res.data))
        .catch(() => {
          sessionStorage.removeItem('advertiser_token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/advertiser/login`, { email, password });
    // 2FA gate — don't persist session yet; caller will handle the second step.
    if (res.data?.requires_totp) return res.data;
    sessionStorage.setItem('advertiser_token', res.data.access_token);
    setAdvertiser(res.data.advertiser);
    analytics.identify(res.data.advertiser?.id);
    analytics.track('advertiser_login', { method: 'password' });
    return res.data;
  };

  const verifyTotpLogin = async (challenge_token, code) => {
    const res = await axios.post(`${API_URL}/api/advertiser/login/verify-totp`, {
      challenge_token, code,
    });
    sessionStorage.setItem('advertiser_token', res.data.access_token);
    setAdvertiser(res.data.advertiser);
    analytics.identify(res.data.advertiser?.id);
    analytics.track('advertiser_login', { method: 'password_totp' });
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API_URL}/api/advertiser/register`, data);
    analytics.track('advertiser_sign_up');
    return res.data;
  };

  const logout = async () => {
    analytics.track('advertiser_logout');
    analytics.clearIdentity();
    const token = sessionStorage.getItem('advertiser_token');
    // Fix 8: Revoke token server-side before clearing local state
    if (token) {
      try {
        await axios.post(`${API_URL}/api/advertiser/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {
        // Proceed with local logout even if the server call fails
      }
    }
    sessionStorage.removeItem('advertiser_token');
    setAdvertiser(null);
  };

  const updateProfile = async (data) => {
    const token = sessionStorage.getItem('advertiser_token');
    const res = await axios.put(`${API_URL}/api/advertiser/me`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAdvertiser(res.data);
    return res.data;
  };

  return (
    <AdvertiserAuthContext.Provider value={{ advertiser, loading, login, verifyTotpLogin, register, logout, updateProfile }}>
      {children}
    </AdvertiserAuthContext.Provider>
  );
}

export const useAdvertiserAuth = () => useContext(AdvertiserAuthContext);

// Axios instance for advertiser API calls
export const advertiserApi = axios.create({
  baseURL: API_URL,
});

// Fix 7: Read token from sessionStorage (not localStorage)
advertiserApi.interceptors.request.use(config => {
  const token = sessionStorage.getItem('advertiser_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
